import axios from "axios";

import config from "../../config";
import { clearUser, track } from "../../analytics";
import { EVENTS } from "../../analytics/events";
import { AUTH_LIMIT, AuthLimitError, authLimitFrom } from "./authLimit";

const REFRESH_TOKEN_URL = "/agent-api/auth/refresh-token";

const AUTH_SESSION_KEY = "concierge_user";
export const getUserFromCookie = () => {
    const user = sessionStorage.getItem(AUTH_SESSION_KEY);
    return user ? (typeof user == "object" ? user : JSON.parse(user)) : null;
};

// An expired session ends the session with a full document load, which destroys
// every React effect — so the analytics side of it has to happen here, before the
// navigation, or it never happens at all (ANALYTICS.md B3). Order matters: emit the
// event while the user dimensions are still set, then clear them.
const redirectToLogin = () => {
    try {
        track(EVENTS.LOGOUT, { reason: "session_expired" });
        clearUser();
    } catch {
        // Analytics must never be able to break the auth redirect.
    }
    window.location.href = "/auth/login";
};

// Every path below that hands a message back to the UI reports it once. The HTTP
// status ONLY: an API error message can quote a field value, a customer name or a
// document reference, so the text itself never travels (ANALYTICS.md §9). A 401 is
// not reported here — it is a session expiry, already covered by the `logout` event
// above, and it never reaches a component as an error.
const trackApiError = (code: string | number) => {
    try {
        track(EVENTS.ERROR_SHOWN, { error_scope: "api", error_code: code });
    } catch {
        // Analytics must never be able to change how an API error surfaces.
    }
};

// content type
axios.defaults.headers.post["Content-Type"] = "application/json";
axios.defaults.baseURL = config.API_URL;

// intercepting to capture errors
axios.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error) => {
        // Any status outside 2xx triggers this. Every path must end in a retry or a
        // rejected STRING — never fall through to `undefined` (callers read
        // `response.data` and would crash with "response is undefined"), and never
        // reject a raw Error/object (rendering one throws "Objects are not valid as a
        // React child (found: [object Error])").
        const requestConfig = error?.config || {};
        const requestUrl = requestConfig?.url || "";

        // Sign-in pages and the refresh call opt in to the rate-limit details
        // (authLimit.ts). They handle the object; everyone else still gets a string.
        if (requestConfig?.[AUTH_LIMIT]) {
            const limited = authLimitFrom(error);
            if (limited) {
                trackApiError(limited.status);
                return Promise.reject(limited);
            }
        }

        // Avoid retrying the refresh endpoint itself.
        if (requestUrl?.includes(REFRESH_TOKEN_URL)) {
            return Promise.reject("Unauthorized");
        }

        // 401 → one-shot token refresh, then replay the original request.
        if (error?.response?.status === 401) {
            try {
                const user = getUserFromCookie();
                if (!user?.refresh_token) {
                    throw new Error("No refresh token available");
                }

                const refresh = await axios.post(
                    REFRESH_TOKEN_URL,
                    {refresh_token: user.refresh_token},
                    {[AUTH_LIMIT]: true} as any,
                );
                if (refresh?.data?.status) {
                    user.refresh_token = refresh?.data?.data?.refresh_token;
                    user.access_token = refresh?.data?.data?.access_token;
                    sessionStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(user));
                    // Replay with the refreshed token.
                    const config = error.config;
                    setAuthorization(user.access_token);
                    config.headers["Authorization"] = `Bearer ${user.access_token}`;
                    return axios.request(config);
                }
            } catch (e) {
                // Rate limited (429) or the limiter is down (503): the session is still
                // good, so keep it. Anything else falls through to the redirect below.
                if (e instanceof AuthLimitError) {
                    return Promise.reject(e.message);
                }
            }
            redirectToLogin();
            return Promise.reject("Unauthorized");
        }

        // Everything else → reject with a human-readable string.
        let message;
        switch (error?.response?.status) {
            case 404:
                message = "Sorry! the data you are looking for could not be found";
                break;
            default:
                message =
                    error?.response?.data?.message
                        || error?.response?.data?.data
                        || error?.message
                        || "Something went wrong";
        }
        trackApiError(error?.response?.status ?? "network");
        return Promise.reject(
            typeof message === "string" ? message : (message?.message || "Something went wrong")
        );
    }
);


/**
 * Sets the default authorization
 * @param {*} token
 */
const setAuthorization = (token: string | null) => {
    if (token) axios.defaults.headers.common["Authorization"] = "Bearer " + token;
    else delete axios.defaults.headers.common["Authorization"];
};

class APICore {
    /**
     * Fetches data from given url
     */
    get = (url: string, params: any, cancelToken?: any) => {
        let response;
        if (params) {
            var queryString = params
                ? Object.keys(params)
                    .map((key) => key + "=" + params[key])
                    .join("&")
                : "";
            response = axios.get(`${url}?${queryString}`, {params, cancelToken});
        } else {
            response = axios.get(`${url}`, {params, cancelToken});
        }
        return response;
    };

    getFile = (url: string, params: any) => {
        let response;
        if (params) {
            var queryString = params
                ? Object.keys(params)
                    .map((key) => key + "=" + params[key])
                    .join("&")
                : "";
            response = axios.get(`${url}?${queryString}`, {responseType: "blob"});
        } else {
            response = axios.get(`${url}`, {responseType: "blob"});
        }
        return response;
    };

    getMultiple = (urls: string, params: any) => {
        const reqs = [];
        let queryString = "";
        if (params) {
            queryString = params
                ? Object.keys(params)
                    .map((key) => key + "=" + params[key])
                    .join("&")
                : "";
        }

        for (const url of urls) {
            reqs.push(axios.get(`${url}?${queryString}`));
        }
        return axios.all(reqs);
    };

    /**
     * post given data to url
     */
    create = (url: string, data: any, config?: any) => {
        return axios.post(url, data, config);
    };

    /**
     * Updates patch data
     */
    updatePatch = (url: string, data: any) => {
        return axios.patch(url, data);
    };

    /**
     * Updates data
     */
    update = (url: string, data: any) => {
        return axios.put(url, data);
    };

    /**
     * Deletes data
     */
    delete = (url: string) => {
        return axios.delete(url);
    };

    /**
     * post given data to url with file
     */
    createWithFile = (url: string, data: any) => {
        const formData = new FormData();
        for (const k in data) {
            formData.append(k, data[k]);
        }

        const config = {
            headers: {
                "content-type": "multipart/form-data",
            },
        };
        return axios.post(url, formData, config);
    };

    /**
     * post given data to url with file
     */
    updateWithFile = (url: string, data: any) => {
        const formData = new FormData();
        for (const k in data) {
            formData.append(k, data[k]);
        }

        const config = {
            headers: {
                "content-type": "multipart/form-data",
            },
        };
        return axios.patch(url, formData, config);
    };

    isUserAuthenticated = () => {
        const user = this.getLoggedInUser();
        if (!user) {
            return false;
        }
        setAuthorization(user.access_token);
        return true;
    };

    setLoggedInUser = (session: any) => {
        if (session)
            sessionStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(session));
        else {
            sessionStorage.removeItem(AUTH_SESSION_KEY);
        }
    };
    /**
     * Returns the logged in user
     */
    getLoggedInUser = () => {
        return getUserFromCookie();
    };

    setUserInSession = (modifiedUser: any) => {
        let userInfo = sessionStorage.getItem(AUTH_SESSION_KEY);
        if (userInfo) {
            const {access_token, refresh_token, user} = JSON.parse(userInfo);
            this.setLoggedInUser({
                access_token,
                refresh_token,
                ...user,
                ...modifiedUser,
            });
        }
    };
}

/*
Check if token available in session
*/
let user = getUserFromCookie();
if (user) {
    const token = user?.access_token;
    if (token) {
        setAuthorization(token);
    }
}

export {APICore, setAuthorization};
